import React, { memo, ReactNode, useEffect, useState } from "react";
import { ArrayEmitter } from "../../utils/array-emitter";

interface IDialogQueueItem<T> {
	renderDialog: (
		deferred: IDeferred<T>,
		options: {
			isOpen: boolean;
		},
	) => ReactNode;
	id: string;
	deferred: IDeferred<T>;
	/** Allow another dialog to be opened at the same time (eg. login dialog) */
	allowOther?: boolean;
}

interface IDeferred<T> {
	resolve: (result?: T) => void;
	reject: (reason: unknown) => void;
}

const DialogQueue = ({ dialogs }: { dialogs: ArrayEmitter<IDialogQueueItem<unknown>> }) => {
	// Only used to force a rerender when dialogs array is changed
	const [, setIteration] = useState(0);

	const [closingDialogs, setClosingDialogs] = useState<{
		[key: string]: true;
	}>({});

	useEffect(() => {
		const forceUpdate = () => setIteration((val) => val + 1);
		dialogs.on("change", forceUpdate);
		return () => {
			dialogs.off("change", forceUpdate);
		};
	}, [dialogs]);

	const removeDialog = (dialog: IDialogQueueItem<unknown>) => {
		setClosingDialogs({
			...closingDialogs,
			[dialog.id]: true,
		});

		setTimeout(() => {
			// remove the dialog from our array
			const index = dialogs.array.indexOf(dialog);
			if (index !== -1) {
				dialogs.array.splice(index, 1);
			}

			// remove it from the closing state
			const newClosingDialogs = { ...closingDialogs };
			// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
			delete newClosingDialogs[dialog.id];
			setClosingDialogs(newClosingDialogs);
		}, 150);
	};

	// A simple way to show only one at a time
	// We loop through until we find a dialog thats not closing
	// When one is closing, we flip this flag and render all of the other ones in a closed state
	// This ensures we get the proper animations for closing dialogs
	let foundOpen = false;

	return (
		<>
			{dialogs.array.map((dialogItem) => {
				const {
					deferred: { resolve, reject },
					id,
					renderDialog,
					allowOther,
				} = dialogItem;

				const isOpen = !closingDialogs[id] && !foundOpen;

				if (!closingDialogs[id]) {
					// eslint-disable-next-line react-compiler/react-compiler
					foundOpen = !allowOther;
				}

				const RenderedDialog = () => (
					<>
						{renderDialog(
							{
								resolve: (result) => {
									removeDialog(dialogItem);
									resolve(result);
								},
								reject: (reason) => {
									removeDialog(dialogItem);
									reject(reason);
								},
							},
							{
								isOpen,
							},
						)}
					</>
				);

				return <RenderedDialog key={id} />;
			})}
		</>
	);
};

interface IShowDialogOptions {
	allowOther?: boolean;
}

interface IRenderedDialogOptions {
	isOpen: boolean;
}

export const createDialogQueue = () => {
	const dialogs = new ArrayEmitter<IDialogQueueItem<unknown>>();

	let uniqueDialogId = 0;
	function showDialogAsync<T>(
		id: string,
		renderDialog: (deferred: IDeferred<T>, { isOpen }: IRenderedDialogOptions) => ReactNode,
		{ allowOther }: IShowDialogOptions = {},
	) {
		return new Promise<T>((resolve, reject) => {
			const deferred = { resolve, reject } as IDeferred<unknown>;
			const dialogItem: IDialogQueueItem<unknown> = {
				id,
				renderDialog,
				deferred,
				allowOther,
			};
			dialogs.push(dialogItem);
		});
	}

	return {
		DialogQueue: memo(function Queue() {
			return <DialogQueue dialogs={dialogs} />;
		}),
		showDialogAsync: function showDialog<T>(
			renderDialog: (
				deferred: IDeferred<T>,
				options: {
					isOpen: boolean;
				},
			) => ReactNode,
			options?: IShowDialogOptions,
		) {
			uniqueDialogId += 1;
			return showDialogAsync(uniqueDialogId.toString(), renderDialog, options);
		},
	};
};
